import { useState } from 'react';
import { useLoaderData } from 'react-router';

import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import type { loader } from '@modules/professional/page/loaders/overview-page-loader.server';
import {
    detailTileActionsFor,
    usePrefersReducedMotion,
} from '~/ui-rocco/pages/detail-shared';
import type { AboutFeature } from '../utils/about-feature';
import { resolveAboutFeature } from '../utils/about-feature';
import { getFeaturedWork } from '../utils/featured-work';
import { toEmbedUrl } from '../utils/video-embed';
import { NAV_DARK_GROUND } from '../hooks/use-band-over-dark';
import { TileOverlayWithFollowSelector } from '~/modules/moodboards/components';

/**
 * Feature media showcase (Figma §3).
 *
 * The feature media sits on white and overlaps the dark band below — the black
 * background starts ~84% down the image (Figma: image y2198–3318, black bg top
 * y3140), so the media "sticks out" of the dark band. The band holds the
 * caption and then carries straight into the Selected-work section (also
 * dark), which owns the run of black between them.
 *
 * The media is whatever the brand authored in Business Manager, falling back to
 * their lead featured project. See {@link resolveAboutFeature} for that rule —
 * in particular why the authored copy never mixes with the derived project's.
 */
interface ProfessionalAboutSectionProps {
    /**
     * Whether Selected work renders below. It owns the run of black under the
     * caption, so when it stands down this band has to close itself instead —
     * the two sections choose their items differently, and a professional whose
     * only work is associated projects reaches this one but not that one.
     */
    hasSelectedWork?: boolean;
}

export const ProfessionalAboutSection = ({
    hasSelectedWork = true,
}: ProfessionalAboutSectionProps = {}) => {
    const { professionalOverview, featuredProjects, associatedProjects } =
        useLoaderData<typeof loader>();

    // Product suppliers (e.g. Resene) have no projects of their own — fall back
    // to associated projects so the feature image isn't empty.
    const lead = getFeaturedWork(
        featuredProjects.featuredProjectsList,
        associatedProjects,
    )[0];
    const feature = resolveAboutFeature(professionalOverview, lead);

    // Without media there is no image and no caption, so the band would render
    // as an empty dark strip.
    if (!feature) return null;

    // Tile actions exist only for the derived project — authored media is the
    // brand's own, with no pin, enquiry target or third-party website.
    const leadActions =
        feature.kind === 'derived' && lead
            ? detailTileActionsFor({
                  id: lead.id,
                  link: lead.link,
                  title: lead.title,
                  images: lead.images ?? [],
                  pinType: 'ProjectPin',
                  professional: lead.professional,
                  professionalLink: lead.professional?.link,
                  website: lead.website,
              })
            : null;

    return (
        // A derived project image is a desktop magazine device, and not this
        // brand's to lead with on a small screen — so the band is desktop-only
        // until the brand authors its own media, which earns a place in front
        // of the majority-mobile audience.
        <section
            className={cn(
                'relative w-full',
                !feature.showOnMobile && 'max-md:hidden',
            )}
        >
            {/* Feature media — on white, overlapping the dark band (kept on top). */}
            <div className="relative z-10 mx-auto w-full max-w-[120rem] md:px-[3rem]">
                {leadActions && lead ? (
                    <RoccoLink
                        to={lead.link}
                        className={cn(MEDIA_FRAME, 'group')}
                    >
                        <AboutFeatureImage feature={feature} />
                        {/* The largest picture on the page, and until now the
                            only one a reader could do nothing with. */}
                        <TileOverlayWithFollowSelector
                            pinId={lead.id}
                            pinType="ProjectPin"
                            enquiry={leadActions.enquiry}
                            visitWebsite={leadActions.visitWebsite}
                            share={leadActions.share}
                            tileInfo={leadActions.tileInfo}
                        />
                    </RoccoLink>
                ) : (
                    <AboutFeatureMedia feature={feature} />
                )}
            </div>

            {/* Dark band — slid up behind the media's lower ~16% so the media
                sticks out, with pt dropping the caption just below it.
                Selected-work's own top padding is normally the whole 100px run
                of black between the caption and its heading, so this carries a
                bottom padding only when that section is not there to do it. */}
            <div
                {...NAV_DARK_GROUND}
                className={cn(
                    'bg-[#181818] text-white focus-ring-on-dark max-md:pb-6 md:-mt-[13.1rem] md:pt-[16.225rem]',
                    !hasSelectedWork && 'md:pb-[6.25rem]',
                )}
            >
                {feature.caption ? (
                    <div className="mx-auto w-full max-w-[120rem] max-md:px-6 max-md:pt-4 md:pl-[2.3124rem]">
                        <p className="m-0 font-sans leading-tight font-normal text-white max-md:text-base md:text-[1.248rem]">
                            {feature.caption}
                        </p>
                        {feature.subcaption ? (
                            <p className="m-0 font-sans leading-tight text-white/50 max-md:text-sm md:text-[1.08rem]">
                                {feature.subcaption}
                            </p>
                        ) : null}
                    </div>
                ) : null}
            </div>
        </section>
    );
};

const MEDIA_FRAME =
    'bg-neutral-900 relative block w-full overflow-hidden rounded-tile max-md:aspect-[414/300] md:aspect-[1820/1102]';

/**
 * The feature media box, in one of three modes: a still image, an uploaded MP4
 * playing silently on loop, or a hosted video behind a play affordance.
 *
 * A link only wraps the still image. Video is interactive in its own right, so
 * wrapping it would make every attempt to press play navigate away instead.
 */
const AboutFeatureMedia = ({ feature }: { feature: AboutFeature }) => {
    if (feature.video) {
        return <AboutFeatureVideo feature={feature} video={feature.video} />;
    }

    if (!feature.link) {
        return (
            <div className={MEDIA_FRAME}>
                <AboutFeatureImage feature={feature} />
            </div>
        );
    }

    return (
        <RoccoLink to={feature.link} className={MEDIA_FRAME}>
            <AboutFeatureImage feature={feature} />
        </RoccoLink>
    );
};

const AboutFeatureImage = ({ feature }: { feature: AboutFeature }) => (
    <img
        src={feature.src}
        alt={feature.alt}
        className="absolute inset-0 size-full object-cover"
        loading="lazy"
    />
);

const AboutFeatureVideo = ({
    feature,
    video,
}: {
    feature: AboutFeature;
    video: NonNullable<AboutFeature['video']>;
}) => {
    const [playing, setPlaying] = useState(false);
    const reducedMotion = usePrefersReducedMotion();
    const embedUrl = video.isEmbed ? toEmbedUrl(video.url) : null;

    // Reduced motion holds the poster frame rather than starting a silent
    // loop. Hosted video is click-to-play anyway, so it only loses the
    // affordance once the visitor has opted out of motion entirely.
    if (reducedMotion && !video.isEmbed) {
        return (
            <div className={MEDIA_FRAME}>
                <AboutFeatureImage feature={feature} />
            </div>
        );
    }

    if (!video.isEmbed) {
        return (
            <div className={MEDIA_FRAME}>
                <video
                    src={video.url}
                    poster={feature.src}
                    aria-label={feature.alt}
                    className="absolute inset-0 size-full object-cover"
                    preload="auto"
                    autoPlay
                    muted
                    loop
                    playsInline
                />
            </div>
        );
    }

    // An unrecognised host never reaches an iframe — the poster stands in.
    if (!embedUrl) {
        return (
            <div className={MEDIA_FRAME}>
                <AboutFeatureImage feature={feature} />
            </div>
        );
    }

    if (playing) {
        return (
            <div className={MEDIA_FRAME}>
                <iframe
                    src={embedUrl}
                    title={feature.alt || 'Featured video'}
                    className="absolute inset-0 size-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                />
            </div>
        );
    }

    return (
        <button
            type="button"
            onClick={() => setPlaying(true)}
            aria-label={
                feature.caption
                    ? `Play ${feature.caption}`
                    : 'Play featured video'
            }
            className={cn(MEDIA_FRAME, 'group cursor-pointer')}
        >
            <AboutFeatureImage feature={feature} />
            <span className="absolute inset-0 flex items-center justify-center">
                <span className="flex size-[4.5rem] items-center justify-center rounded-full bg-white/90 transition-transform duration-200 group-hover:scale-110 motion-reduce:transition-none">
                    <span className="ml-[0.25rem] border-y-[0.75rem] border-l-[1.25rem] border-y-transparent border-l-[#181818]" />
                </span>
            </span>
        </button>
    );
};
